import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { SwitchThemeContext, NotificationDispatch } from '@tryhackme/thm-ui-components';
import tinymce from 'tinymce';
import { ThemeColorMode } from 'src/common/enums';
import { useEditorSetup } from '../hooks/use-editor-setup';
import { getEditorConfig, loadEditorTheme } from './tinymce-config';
import './plugins/thm-markdown.plugin';
import './plugins/enhanced-code-editor.plugin';
export interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  onImageUpload?: (files: File[], insertImage: (base64: string, callback: (images: HTMLImageElement[]) => void) => void) => void;
  onMediaDelete?: (images: HTMLImageElement[]) => void;
}
declare global {
  interface Window {
    tinymce: typeof import('tinymce');
  }
}
export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  content,
  onChange,
  onImageUpload,
  onMediaDelete
}) => {
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const {
    editorInstanceRef,
    isInitializedRef,
    handleEditorInit
  } = useEditorSetup(content, onChange, onMediaDelete);
  const handleImageUpload = useCallback(async (blobInfo: {
    blob: () => Blob;
    filename: () => string;
  }) => {
    const blob = blobInfo.blob();
    const file = new File([blob], blobInfo.filename() || 'image.png', {
      type: blob.type
    });
    return new Promise<string>(resolve => {
      onImageUpload?.([file], (base64: string, callback: (images: HTMLImageElement[]) => void) => {
        resolve(base64);
        requestAnimationFrame(() => {
          const images = editorInstanceRef.current?.dom.select(`img[src="${base64}"]`);
          if (images?.length) callback((images as HTMLImageElement[]));
        });
      });
    });
  }, [editorInstanceRef, onImageUpload]);
  const initializeEditor = useCallback(async () => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.destroy();
      editorInstanceRef.current = null;
    }
    isInitializedRef.current = false;
    await loadEditorTheme(isDarkMode);
    if (!window.tinymce) {
      return;
    }
    const config = {
      ...getEditorConfig(isDarkMode),
      target: editorRef.current || undefined,
      images_upload_handler: handleImageUpload,
      automatic_uploads: true,
      file_picker_types: 'file image media',
      setup: handleEditorInit
    };
    await tinymce.init(config);
  }, [editorInstanceRef, isInitializedRef, isDarkMode, handleImageUpload, handleEditorInit]);
  useEffect(() => {
    const init = () => {
      initializeEditor().catch(() => {
        NotificationDispatch('error', 'Error initializing editor');
      });
    };
    init();
    return () => {
      editorInstanceRef?.current?.destroy();
      editorInstanceRef.current = null;
      isInitializedRef.current = false;
    };
  }, [editorInstanceRef, initializeEditor, isInitializedRef]);
  return <textarea ref={editorRef} data-testid="tinymce-editor" aria-label="task editor" data-sentry-component="RichTextEditor" data-sentry-source-file="index.tsx" />;
};